/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { toast } from 'react-toastify';

import { Button, Form, Grid, Icon, Modal } from '@mairistem/composants';
import { SectionHeader } from '@mairistem/administres-utils';

import { URL_ETATS_MATRIMONIAUX_PARENTS_MARIAGE } from '@/constants';
import { useOptionsReferentiel } from '@/hooks';
import { buildEtatMatrimonialParentsUpdatePayload } from '@/utils';

interface ModalEtatMatrimonialParentsProps {
  open: boolean;
  /** Identifiant de l'ACTE : construit l'URL de `service.update`. */
  identifiant?: number;
  /** L'acte : porte l'état matrimonial des deux couples de parents. */
  acte?: any;
  /** 1 ou 2 : quel couple de parents, donc quelle colonne. */
  numeroConjoint: 1 | 2;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  /** Rafraîchit l'acte après écriture — la section le relit pour s'afficher. */
  onActeSaved?: (acte: any) => void;
  onSaved?: (acte: any) => void;
}

/**
 * Section « ETAT MATRIMONIAL DES PARENTS » d'un onglet de parents.
 *
 * ⚠️ Une seule situation pour DEUX personnes : elle qualifie le couple, et
 * `ec_actm` ne la porte qu'une fois par conjoint. C'est pourquoi elle n'est ni
 * dans le bloc du parent 1 ni dans celui du parent 2, mais entre les deux — avec
 * son propre crayon.
 *
 * ⚠️ Un seul appel, sur l'ACTE : rien ici n'appartient à un individu. C'est ce
 * qui la distingue de `ModalParent`, et pourquoi sa section déclare sa modale
 * (cf. `GroupeSections.modaleDeSection`).
 *
 * Le référentiel des parents est plus large que celui des conjoints :
 * « marié(e) » et « séparé(e) » n'existent que là.
 */
const ModalEtatMatrimonialParents = (
  props: ModalEtatMatrimonialParentsProps,
) => {
  const {
    open,
    identifiant,
    acte,
    numeroConjoint,
    service,
    onClose,
    onActeSaved,
    onSaved,
  } = props;

  const cleCode = `parentsConj${numeroConjoint}EtatMatrimonialCode`;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);

  const { options } = useOptionsReferentiel(
    open ? URL_ETATS_MATRIMONIAUX_PARENTS_MARIAGE : undefined,
    'codeJustice',
  );

  React.useEffect(() => {
    if (!open) {
      return;
    }
    // Le contrôleur expose le code BRUT, pas le vocabulaire du Cases.
    setData({ etatMatrimonialParents: acte?.[cleCode] });
  }, [open, acte, cleCode]);

  const handleChange = React.useCallback(
    (_event: React.SyntheticEvent | null, { value }: { value: unknown }) => {
      setData({ etatMatrimonialParents: value });
    },
    [],
  );

  const handleSave = React.useCallback(() => {
    setSaving(true);

    const payload = buildEtatMatrimonialParentsUpdatePayload(
      identifiant as number,
      numeroConjoint,
      data,
    );

    const enregistrement = service?.update(payload);

    if (!enregistrement) {
      setSaving(false);
      onClose();
      return;
    }

    enregistrement
      .then(() => {
        const acteAJour = { ...acte, ...payload.acte };
        onActeSaved?.(acteAJour);
        onSaved?.(acteAJour);
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de l'état matrimonial des parents",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    service,
    identifiant,
    numeroConjoint,
    acte,
    data,
    onActeSaved,
    onSaved,
    onClose,
  ]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Modifier l'état matrimonial des parents" />
      {/*
        ⚠️ La classe est posée sur le CONTENU, et non sur la modale : le `Modal`
        de `@mairistem/composants` RECONSTRUIT son `className` à partir de ses
        propres drapeaux (`Modal.js`, « hasHeader » / « parametrage ») et jette
        celui qu'on lui passe. `Modal.Content`, lui, transmet le sien.

        `liste-debordante` laisse la liste flotter par-dessus le pied au lieu de
        s'insérer dans le flux et d'ajouter un défilement — cf. `index.less`.
      */}
      <Modal.Content className="liste-debordante">
        {/* @ts-ignore */}
        <Form>
          {/* @ts-ignore */}
          <SectionHeader
            title="État matrimonial des parents"
            required={false}
          />
          <Grid>
            <Grid.Row style={{ paddingLeft: '1em' }}>
              <Grid.Column computer={10} tablet={16} mobile={16}>
                <Form.Dropdown
                  selection
                  clearable
                  fluid
                  name="etatMatrimonialParents"
                  label="Etat matrimonial des parents"
                  placeholder="Choisir une situation"
                  options={options}
                  value={data?.etatMatrimonialParents ?? ''}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="etat-matrimonial-parents-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const ModalEtatMatrimonialParents1 = (props: Record<string, any>) => (
  <ModalEtatMatrimonialParents {...(props as any)} numeroConjoint={1} />
);

export const ModalEtatMatrimonialParents2 = (props: Record<string, any>) => (
  <ModalEtatMatrimonialParents {...(props as any)} numeroConjoint={2} />
);

export default ModalEtatMatrimonialParents;
